import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { logger } from "../lib/logger";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Lock, CheckCircle2, Sparkles, Loader2, CreditCard, ExternalLink, XCircle, Star, Clock } from "lucide-react";
import { ReviewReplyDialog } from "../components/ReviewReplyDialog";

function PromoCountdown({ endsAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = new Date(endsAt).getTime();
  const diff = end - now;
  if (!endsAt || Number.isNaN(end) || diff <= 0) return null;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff / (60 * 60 * 1000)) % 24);
  const mins = Math.floor((diff / (60 * 1000)) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  const seg = (n, label) => (
    <div className="flex flex-col items-center px-2 sm:px-3 py-1.5 rounded-md bg-charcoal/5 border border-charcoal/10 min-w-[52px]">
      <span className="font-display text-xl leading-none text-charcoal tabular-nums" data-testid={`promo-countdown-${label}`}>
        {String(n).padStart(2, "0")}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-charcoal/50 mt-0.5">{label}</span>
    </div>
  );
  return (
    <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="promo-countdown">
      <Clock className="w-4 h-4 text-terracotta" />
      <span className="text-sm text-charcoal/70 font-medium mr-1">Offer ends in</span>
      {seg(days, "days")}
      {seg(hours, "hrs")}
      {seg(mins, "min")}
      {seg(secs, "sec")}
    </div>
  );
}

export default function HandymanDashboard() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [profile, setProfile] = useState(null);
  const [quote, setQuote] = useState(null);
  const [activating, setActivating] = useState(false);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [form, setForm] = useState({
    bio: "", services: [], hourly_rate: 40, campus: "", city: "",
    years_experience: 0, photo_url: "",
  });
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    if (user) {
      api.get(`/handymen/${user.user_id}`).then((r) => {
        setProfile(r.data);
        setForm({
          bio: r.data.bio || "", services: r.data.services || [],
          hourly_rate: r.data.hourly_rate || 40,
          campus: r.data.campus || user.campus || "", city: r.data.city || "",
          years_experience: r.data.years_experience || 0,
          photo_url: r.data.photo_url || user.picture || "",
        });
      }).catch(() => setProfile(null));
      api.get("/handyman/activation/quote").then((r) => setQuote(r.data)).catch(() => {});
      api.get("/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect({ connected: false }));
      api.get("/reviews/mine").then((r) => setReviews(r.data)).catch(() => setReviews([]));
    }
    // If returning from Stripe onboarding, refresh connect status
    if (sp.get("connect") === "return") {
      toast.success("Welcome back — refreshing your Stripe status…");
    }
  }, [user, sp]);

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "handyman") return <Navigate to="/dashboard" replace />;

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id) ? f.services.filter((s) => s !== id) : [...f.services, id],
    }));
  };

  const save = async () => {
    try {
      await api.put("/handymen/me", { ...form, campus: "UW-Madison", hourly_rate: Number(form.hourly_rate), years_experience: Number(form.years_experience) });
      toast.success("Profile saved!");
      // re-fetch quote (years_experience may have changed the discount)
      const { data } = await api.get("/handyman/activation/quote");
      setQuote(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const activate = async () => {
    setActivating(true);
    try {
      const { data } = await api.post("/handyman/activation/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setActivating(false);
    }
  };

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Stripe onboarding");
      setConnecting(false);
    }
  };

  const refreshQuote = async () => {
    try { const q = await api.get("/handyman/activation/quote"); setQuote(q.data); }
    catch (e) {
      logger.warn("Failed to refresh activation quote", e);
      Sentry.captureException(e);
    }
  };
  const cancelSub = async () => {
    try {
      const { data } = await api.post("/handyman/subscription/cancel");
      toast.success(data.message || "Cancellation scheduled");
      await refreshQuote();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  const resumeSub = async () => {
    try {
      const { data } = await api.post("/handyman/subscription/resume");
      toast.success(data.message || "Cancellation reversed");
      await refreshQuote();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resume"); }
  };
  const pauseSub = async (days) => {
    try {
      const { data } = await api.post("/handyman/subscription/pause", { days });
      toast.success(data.message || "Paused");
      await refreshQuote();
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not pause"); }
  };
  const unpauseSub = async () => {
    try {
      const { data } = await api.post("/handyman/subscription/unpause");
      toast.success(data.message || "Welcome back!");
      await refreshQuote();
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resume"); }
  };

  const isActive = user.handyman_active || quote?.handyman_active;

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-display text-4xl text-charcoal">Your Move-in Pro profile</h1>
        <p className="text-charcoal/70 mt-2">Keep it real & honest — students trust this page.</p>

        {/* Punctuality reminder for the Pro */}
        {isActive && (
          <div className="mt-6 rounded-2xl border border-terracotta/30 bg-[#131C2E] p-4 lg:p-5 flex items-start gap-3" data-testid="pro-punctuality-notice">
            <div className="w-9 h-9 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <p className="font-display text-lg text-charcoal leading-tight">Move-in demand is heavy — punctuality is everything.</p>
              <p className="text-sm text-charcoal/70 mt-1 leading-relaxed">
                Please arrive at the scheduled time. Late or missed appointments cost the student their move-in slot and hurt your rating. If you have extra time after finishing, you can keep working — any extra hours are auto-billed at your hourly rate when the student marks the job complete.
              </p>
            </div>
          </div>
        )}

        {/* Activation banner */}
        {quote && !isActive && (
          <div className="mt-8 rounded-2xl border border-terracotta/40 bg-gradient-to-br from-[#0F1F2E] to-[#131C2E] p-6 lg:p-8 animate-fade-up" data-testid="activation-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-2xl text-charcoal">Activate your Move Day Pro account</h2>
                <p className="text-charcoal/70 mt-1">
                  Your profile is live for previews, but students can't book you until your annual membership is active.
                </p>
                <div className="mt-5 flex items-baseline gap-3 flex-wrap">
                  {(quote.discount_eligible || quote.promo_eligible) && (
                    <span className="text-lg text-charcoal/50 line-through" data-testid="quote-full-price">${quote.full_price.toFixed(0)}</span>
                  )}
                  <span className="font-display text-4xl text-terracotta" data-testid="quote-amount">${quote.amount.toFixed(0)}</span>
                  <span className="text-charcoal/60">/ year</span>
                  {quote.promo_eligible ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-terracotta/15 text-terracotta text-xs px-3 py-1 font-medium" data-testid="promo-badge">
                      <Sparkles className="w-3 h-3" /> Launch promo — year 1 free
                    </span>
                  ) : quote.discount_eligible && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-terracotta/15 text-terracotta text-xs px-3 py-1 font-medium">
                      <Sparkles className="w-3 h-3" /> 50% Startup Discount
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-charcoal/60" data-testid="quote-reason">{quote.discount_reason}</p>

                {quote.promo_eligible && quote.promo_end && (
                  <PromoCountdown endsAt={quote.promo_end} />
                )}

                <ul className="mt-5 space-y-2 text-sm text-charcoal/80">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sage" /> Listed in campus browse & search</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sage" /> Accept bookings & get paid via Stripe</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sage" /> Full year of unlimited move-in jobs</li>
                </ul>

                <Button onClick={activate} disabled={activating} data-testid="activate-btn"
                  className="mt-6 rounded-full bg-terracotta hover:bg-terracotta/90 text-white btn-lift h-12 px-8">
                  {activating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>) : (<>Activate — pay ${quote.amount.toFixed(0)}</>)}
                </Button>
              </div>
            </div>
          </div>
        )}

        {isActive && (
          <div className="mt-8 rounded-2xl border border-sage/40 bg-sage/10 p-5 flex items-center gap-3 flex-wrap" data-testid="active-banner">
            <CheckCircle2 className="w-6 h-6 text-sage shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <p className="font-display text-lg text-charcoal">
                {quote?.cancel_at_period_end ? "Membership ending soon" : "Your account is active"}
              </p>
              <p className="text-sm text-charcoal/70">
                {quote?.cancel_at_period_end
                  ? `You'll keep all benefits until ${quote?.subscription_expires_at ? new Date(quote.subscription_expires_at).toLocaleDateString() : "expiry"}. No auto-renewal.`
                  : (user.subscription_expires_at || quote?.subscription_expires_at)
                    ? `Renews ${new Date(user.subscription_expires_at || quote.subscription_expires_at).toLocaleDateString()}`
                    : "You're all set to accept bookings."}
              </p>
            </div>
            {quote?.cancel_at_period_end ? (
              <Button onClick={resumeSub} data-testid="resume-btn" variant="outline"
                className="rounded-full border-sage/40 text-sage hover:bg-sage/15 h-10">
                Resume membership
              </Button>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="open-pause-btn"
                      className="text-sm text-charcoal/60 hover:text-cognac underline decoration-dotted underline-offset-4">
                      Pause membership
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#131C2E] border-[#1F2A40] text-charcoal">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display text-2xl">Pause your membership</AlertDialogTitle>
                      <AlertDialogDescription className="text-charcoal/70">
                        Taking a break? Pause for up to 60 days. You won't receive new bookings while paused, and we'll extend your paid year by the same number of days when you come back.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="flex gap-2 py-2">
                      <Button onClick={() => pauseSub(14)} variant="outline" data-testid="pause-14"
                        className="rounded-full border-[#1F2A40] hover:bg-[#0B1220]">14 days</Button>
                      <Button onClick={() => pauseSub(30)} variant="outline" data-testid="pause-30"
                        className="rounded-full border-[#1F2A40] hover:bg-[#0B1220]">30 days</Button>
                      <Button onClick={() => pauseSub(60)} variant="outline" data-testid="pause-60"
                        className="rounded-full border-[#1F2A40] hover:bg-[#0B1220]">60 days</Button>
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full bg-[#0B1220] border-[#1F2A40] text-charcoal hover:bg-[#1F2A40]">
                        Never mind
                      </AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="open-cancel-btn"
                      className="text-sm text-charcoal/60 hover:text-terracotta underline decoration-dotted underline-offset-4">
                      Cancel membership
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#131C2E] border-[#1F2A40] text-charcoal">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display text-2xl">Cancel your membership?</AlertDialogTitle>
                      <AlertDialogDescription className="text-charcoal/70">
                        You'll keep every benefit — bookings, payouts, dashboard — until {quote?.subscription_expires_at ? new Date(quote.subscription_expires_at).toLocaleDateString() : "your current period ends"}.
                        No refunds are issued, and you can resume anytime before then.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full bg-[#0B1220] border-[#1F2A40] text-charcoal hover:bg-[#1F2A40]" data-testid="keep-membership-btn">
                        Keep membership
                      </AlertDialogCancel>
                      <AlertDialogAction onClick={cancelSub} data-testid="confirm-cancel-btn"
                        className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">
                        <XCircle className="w-4 h-4 mr-2" /> Cancel at period end
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        )}

        {quote?.paused_at && (
          <div className="mt-4 rounded-2xl border border-cognac/40 bg-cognac/10 p-5 flex items-center gap-3 flex-wrap" data-testid="paused-banner">
            <div className="w-8 h-8 rounded-full bg-cognac/20 text-cognac flex items-center justify-center shrink-0">⏸</div>
            <div className="flex-1 min-w-[220px]">
              <p className="font-display text-lg text-charcoal">Membership paused</p>
              <p className="text-sm text-charcoal/70">
                Auto-resumes on {quote?.resume_at ? new Date(quote.resume_at).toLocaleDateString() : "your chosen date"}. Your paid year is extended by the paused days.
              </p>
            </div>
            <Button onClick={unpauseSub} data-testid="unpause-btn"
              className="rounded-full bg-cognac hover:bg-cognac/90 text-midnight h-10">
              Resume now
            </Button>
          </div>
        )}

        {/* Stripe Connect (payouts) */}
        {isActive && connect && (
          <div className="mt-6 rounded-2xl border border-[#1F2A40] bg-[#131C2E] p-6" data-testid="connect-section">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-sage/15 text-sage flex items-center justify-center shrink-0">
                <CreditCard className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-xl text-charcoal">Payouts via Stripe</h3>
                  {connect.charges_enabled ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-sage/15 text-sage text-xs px-2.5 py-1 font-medium" data-testid="connect-enabled">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </span>
                  ) : connect.connected ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-cognac/15 text-cognac text-xs px-2.5 py-1 font-medium" data-testid="connect-pending">
                      Onboarding in progress
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-terracotta/15 text-terracotta text-xs px-2.5 py-1 font-medium" data-testid="connect-not-started">
                      Action required
                    </span>
                  )}
                </div>
                <p className="text-charcoal/70 mt-1 text-sm">
                  You keep <strong className="text-charcoal">75%</strong> of every booking; Move Day Pro's keeps a 25% platform fee.
                  Connect your Stripe account to receive payouts and manage your full Stripe dashboard.
                </p>
                {!connect.charges_enabled && (
                  <Button onClick={startConnect} disabled={connecting} data-testid="connect-btn"
                    className="mt-4 rounded-full bg-sage hover:bg-sage/90 text-midnight btn-lift h-11 px-6">
                    {connecting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>) : (
                      <><ExternalLink className="w-4 h-4 mr-2" /> {connect.connected ? "Continue Stripe onboarding" : "Set up Stripe payouts"}</>
                    )}
                  </Button>
                )}
                {connect.charges_enabled && (
                  <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 mt-3 text-sm text-sage hover:text-sage/80" data-testid="connect-dashboard-link">
                    Open my Stripe dashboard <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-8 space-y-5">
          <div>
            <Label>Photo URL</Label>
            <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
              data-testid="hp-photo" className="mt-1 rounded-xl h-11" placeholder="https://…" />
            {form.photo_url && <img src={form.photo_url} alt="" className="mt-3 w-24 h-24 rounded-full object-cover border border-[#1F2A40]" />}
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="hp-bio" rows={4} className="mt-1 rounded-xl" placeholder="Tell students about yourself…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Hourly rate ($)</Label>
              <Input type="number" min={15} value={form.hourly_rate}
                onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                data-testid="hp-rate" className="mt-1 rounded-xl h-11" />
            </div>
            <div>
              <Label>Years experience</Label>
              <Input type="number" min={0} value={form.years_experience}
                onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                data-testid="hp-years" className="mt-1 rounded-xl h-11" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Campus</Label>
              <Input value="UW-Madison" disabled data-testid="hp-campus" className="mt-1 rounded-xl h-11 opacity-80" />
              <p className="text-[11px] text-charcoal/60 mt-1">Move-in Pros are UW-Madison only during launch.</p>
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="hp-city" className="mt-1 rounded-xl h-11" placeholder="Madison, WI" />
            </div>
          </div>
          <div>
            <Label>Services</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {cats.map((c) => (
                <label key={c.id} className="flex items-center gap-2 cursor-pointer rounded-xl border border-[#1F2A40] p-3 hover:bg-beige transition-colors">
                  <Checkbox checked={form.services.includes(c.id)} onCheckedChange={() => toggleService(c.id)}
                    data-testid={`hp-service-${c.id}`} />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
          </div>
          <Button onClick={save} data-testid="hp-save"
            className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white btn-lift h-11 px-8">
            Save profile
          </Button>
        </div>

        {/* My reviews — reply publicly to student feedback */}
        <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-6 lg:p-8 mt-8" data-testid="my-reviews-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-charcoal">Your reviews</h2>
            {reviews.length > 0 && (
              <Badge className="rounded-full bg-cognac/20 text-cognac">
                {reviews.length} review{reviews.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          {reviews.length === 0 ? (
            <p className="text-charcoal/60" data-testid="my-reviews-empty">No reviews yet. Once a student marks a booking complete and leaves a rating, it will show up here.</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.review_id} data-testid={`my-review-${r.review_id}`}
                  className="rounded-xl border border-[#1F2A40] bg-cream/[0.03] p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.student_name}</span>
                    <div className="flex">{[...Array(r.rating)].map((_, i) => (
                      <Star key={`${r.review_id}-star-${i}`} className="w-4 h-4 fill-cognac text-cognac" />
                    ))}</div>
                    <span className="text-xs text-charcoal/50 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-charcoal/80 mt-2">{r.comment}</p>
                  {r.reply ? (
                    <div className="mt-3 rounded-lg bg-terracotta/5 border-l-2 border-terracotta pl-3 py-2" data-testid={`my-review-reply-${r.review_id}`}>
                      <p className="text-xs text-terracotta/80 font-medium">Your reply · {new Date(r.reply_at).toLocaleDateString()}</p>
                      <p className="text-charcoal/80 text-sm mt-1">{r.reply}</p>
                    </div>
                  ) : (
                    <ReviewReplyDialog review={r} onReplied={(id, reply, reply_at) => {
                      setReviews((prev) => prev.map((x) => x.review_id === id ? { ...x, reply, reply_at } : x));
                    }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
